import { auth } from "./services/firebase";
import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";

// Lazy load pages
const HomePage = lazy(() => import("./components/HomePage"));
const RegistrationPage = lazy(() => import("./components/RegistrationPage"));
const LoginPage = lazy(() => import("./components/LoginPage"));
const PaymentPage = lazy(() => import("./components/PaymentPage"));
const SuccessPage = lazy(() => import("./components/SuccessPage"));
const DashboardPage = lazy(() => import("./components/DashboardPage"));
const TestPage = lazy(() => import("./components/TestPage"));
const ResetPasswordPage = lazy(() => import("./components/ResetPasswordPage"));
const ResultScreen = lazy(() => import("./components/ResultScreen"));
const ProfilePage = lazy(() => import("./components/ProfilePage"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-50">
    <div className="flex flex-col items-center gap-3 p-6">
      <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
        <span className="text-2xl font-black text-white">PQ</span>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
      </div>
      <p className="text-sm font-medium text-slate-600 mt-2">Loading PrepQube...</p>
    </div>
  </div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const FooterBar = () => {
  const { pathname } = useLocation();
  const hide = pathname.startsWith('/test') || pathname.startsWith('/result');
  if (hide) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t border-gray-200 py-2 text-center z-50">
      <p className="text-[10px] text-gray-500">
        <a href="/privacy-policy" className="text-indigo-600 hover:underline mr-3">Privacy Policy</a>
        <span className="text-gray-300 mx-2">|</span>
        📧 <a href="mailto:grievance@prepqube.com" className="text-indigo-600 hover:underline">grievance@prepqube.com</a>
      </p>
    </div>
  );
};

const FREE_TRIAL_IDS = [
  'reading-comprehension-1',
  'percentage',
  'data-interpretation',
  'cat-full-mock-1'
];


const TestGate = ({ user, children }) => {
  const { topicId } = useParams();
  const isFree = FREE_TRIAL_IDS.includes(topicId);

  // Allow access if logged in OR if the specific test is free
  if (user || isFree) return children;
  
  return <Navigate to="/login" replace />;
};

function App() {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      setUser(firebaseUser);
      setInitializing(false);
    });
    return unsubscribe;
  }, []);

  if (initializing) return <PageLoader />;

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage user={user} setUser={setUser} />} />
          <Route path="/home" element={<HomePage user={user} setUser={setUser} />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage setUser={setUser} />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/payment" element={<PaymentPage user={user} />} />
          <Route path="/dashboard" element={user ? <DashboardPage user={user} /> : <Navigate to="/login" replace />} />
          <Route path="/profile" element={user ? <ProfilePage user={user} /> : <Navigate to="/login" replace />} />
          <Route path="/success" element={user ? <SuccessPage user={user} /> : <Navigate to="/login" replace />} />
          {/* Wrap the test and result pages in the TestGate you created */}
<Route 
  path="/test/:collection/:topicId" 
  element={<TestGate user={user}><TestPage user={user} /></TestGate>} 
/>
<Route 
  path="/result/:topicId" 
  element={<TestGate user={user}><ResultScreen user={user} /></TestGate>} 
/>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <FooterBar />
      </Suspense>
    </BrowserRouter>
  );
}

export default App;